import { useSiteSetupActions } from "@inrange/building-manager-api-client";
import {
  getESysName,
  SiteElectricalSystem,
} from "@inrange/building-manager-api-client/models-site";
import { Icons } from "@inrange/theme-components/icons";
import { CSSProperties } from "react";
import { Card, Spinner } from "react-bootstrap";

const SORACOM_MONITORING_URL = "https://console.soracom.io/";

const ExternalLink = ({
  href,
  label,
}: {
  href: string;
  label: string;
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    style={{ color: "#0d6efd", textDecoration: "none" }}
  >
    {label}
    <img
      alt="view"
      src={Icons.shareBlue}
      style={{
        width: "17px",
        height: "17px",
        marginLeft: "8px",
        marginBottom: "2px",
      }}
    />
  </a>
);

const MonitoringPortalsCard = ({
  esysEntries,
  ingestionByEsys,
  style,
  alwaysRender = false,
}: {
  esysEntries: [string, SiteElectricalSystem][];
  ingestionByEsys: ReturnType<typeof useSiteSetupActions>["ingestionByEsys"];
  style?: CSSProperties;
  alwaysRender?: boolean;
}) => {
  const anyLoading = esysEntries.some(
    ([, esys]) => ingestionByEsys[esys.uuid]?.isLoading
  );

  if (anyLoading) {
    return (
      <Card body style={style}>
        <h6 className="fw-bold mb-2">Monitoring portals</h6>
        <Spinner animation="border" size="sm" />
      </Card>
    );
  }

  const esysGroups: {
    esysLabel: string | null;
    links: { href: string; label: string; order: number }[];
  }[] = [];

  for (const [, esys] of esysEntries) {
    const config = ingestionByEsys[esys.uuid]?.data?.site;
    if (!config?.has_monitoring_config) continue;
    const esysLabel =
      esysEntries.length > 1
        ? `Energy match${getESysName(esysEntries.length, esys)}`
        : null;
    const links: { href: string; label: string; order: number }[] = [];
    if (config.soracom_imsi) {
      links.push({
        href: SORACOM_MONITORING_URL,
        label: "📡 Soracom",
        order: 0,
      });
    }
    if (config.solaredge_site_id) {
      links.push({
        href: `https://monitoring.solaredge.com/one#/commercial/power-flow?siteId=${config.solaredge_site_id}`,
        label: "☀️ SolarEdge",
        order: 1,
      });
    }
    if (config.solis_site_id) {
      links.push({
        href: `https://www.soliscloud.com/station/stationDetails/generalSituation/${config.solis_site_id}`,
        label: "☀️ Solis",
        order: 1,
      });
    }
    if (config.isolarcloud_link) {
      links.push({
        href: config.isolarcloud_link,
        label: "🔋 Sungrow",
        order: 2,
      });
    }
    if (config.data_source_type === "reduxi") {
      links.push({
        href: "https://www.reduxi.app/",
        label: "⚡ Reduxi",
        order: 3,
      });
    }
    if (links.length > 0) {
      links.sort((a, b) => a.order - b.order);
      esysGroups.push({ esysLabel, links });
    }
  }

  if (esysGroups.length === 0) {
    if (!alwaysRender) return null;
    return (
      <Card body style={style}>
        <h6 className="fw-bold mb-2">Monitoring portals</h6>
        <div className="text-muted small">No monitoring portals configured.</div>
      </Card>
    );
  }

  return (
    <Card body style={style}>
      <h6 className="fw-bold mb-2">Monitoring portals</h6>
      <div className="d-flex flex-column gap-3">
        {esysGroups.map(({ esysLabel, links }, gi) => (
          <div key={gi} className="d-flex flex-column gap-1">
            {esysLabel && (
              <span className="fw-semibold">{esysLabel}</span>
            )}
            {links.map(({ href, label }, li) => (
              <ExternalLink key={li} href={href} label={label} />
            ))}
          </div>
        ))}
      </div>
    </Card>
  );
};

export default MonitoringPortalsCard;
